import {lazy} from 'react'
import {createBrowserRouter, Navigate, RouterProvider} from 'react-router-dom'
import { YANDEX_MAPS_API_KEY } from '@/config/yandex'
import {ThemeProvider} from './components/theme-provider'
import {AuthProvider} from './contexts/AuthContext'
import {ProtectedRoute} from './components/ProtectedRoute'
import {LazyBoundary, NotFoundPage, RouteErrorPage} from './components/RouteFallbacks'



import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {toApiError} from "@/lib/apiError";
import {toast} from "@/hooks/use-toast";
import {ErrorBoundary} from "@/components/ErrorBoundary";
import {YMaps} from 'react-yandex-maps';
import {Toaster} from "@/components/ui/toaster";

const LoginPage = lazy(() => import('./pages/LoginPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const InfluencersPage = lazy(() => import('./pages/InfluencersPage'))
const BlockedInfluencersPage = lazy(() => import('./pages/BlockedInfluencersPage'))
const RejectedInfluencersPage = lazy(() => import('./pages/RejectedInfluencersPage'))
const EventsPage = lazy(() => import('./pages/EventsPage'))
const CreateEventPage = lazy(() => import('./pages/CreateEventPage'))
const EditEventPage = lazy(() => import('./pages/EditEventPage'))
const SignUpRequestsPage = lazy(() => import('./pages/SignUpRequestsPage'))
const EventRequestsPage = lazy(() => import('./pages/EventRequestsPage'))
const CategoriesPage = lazy(() => import('./pages/CategoriesPage'))
const BannersPage = lazy(() => import('./pages/BannersPage'))
const OrganizationsPage = lazy(() => import('./pages/OrganizationsPage'))
const LeadsPage = lazy(() => import('./pages/LeadsPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const AdminManagementPage = lazy(() => import('./pages/AdminManagementPage'))
const InfluencerDetailPage = lazy(() => import('./pages/InfluencerDetailPage'))
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'))
const RequestDetailPage = lazy(() => import('./pages/RequestDetailPage'))
const InfluencerRequestDetailPage = lazy(() => import('./pages/InfluencerRequestDetailPage'))
const EventRequestDetailPage = lazy(() => import('./pages/EventRequestDetailPage'))
const InfluencerEditPage = lazy(() => import('./pages/InfluencerEditPage'))
const RequestEditPage = lazy(() => import('./pages/RequestEditPage'))
const InfluencerRequestEditPage = lazy(() => import('./pages/InfluencerRequestEditPage'))
const EventRequestEditPage = lazy(() => import('./pages/EventRequestEditPage'))
const SignUpAttemptsPage = lazy(() => import('./pages/SignUpAttemptsPage'))
const SignUpJourneyDetailPage = lazy(() => import('./pages/SignUpJourneyDetailPage'))
const InstagramLoginPage = lazy(() => import('./pages/InstagramLoginPage'))
const InstagramCallbackPage = lazy(() => import('./pages/InstagramCallbackPage'))
const InstagramCallbackWelcomePage = lazy(() => import('./pages/InstagramCallbackWelcomePage'))
const InstagramInsightsPage = lazy(() => import('./pages/InstagramInsightsPage'))
const InfluencerContactsPage = lazy(() => import('./pages/InfluencerContactsPage'))
const InfluencerStatisticsPage = lazy(() => import('./pages/InfluencerStatisticsPage'))
const CountriesPage = lazy(() => import('./pages/CountriesPage'))
const AcademyPage = lazy(() => import('./pages/AcademyPage'))
const AcademyCourseDetailPage = lazy(() => import('./pages/AcademyCourseDetailPage'))
const OrganizationSignUpRequestsPage = lazy(() => import('./pages/OrganizationSignUpRequestsPage'))
const EventTaskSubmissionsPage = lazy(() => import('./pages/EventTaskSubmissionsPage'))
const WithdrawalRequestsPage = lazy(() => import('./pages/WithdrawalRequestsPage'))
const WithdrawalDetailPage = lazy(() => import('./pages/WithdrawalDetailPage'))
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'))
const RewardsPage = lazy(() => import('./pages/RewardsPage'))
const WithdrawalRequestsV2Page = lazy(() => import('./pages/WithdrawalRequestsV2Page'))
const InfluencerWalletPage = lazy(() => import('./pages/InfluencerWalletPage'))
const OrganizationDetail = lazy(() => import('./pages/OrganizationDetail').then((m) => ({ default: m.OrganizationDetail })))
const BranchDetailPage = lazy(() => import('./pages/BranchDetailPage').then((m) => ({ default: m.BranchDetailPage })))
const LeadDetail = lazy(() => import('./pages/LeadDetail').then((m) => ({ default: m.LeadDetail })))

const router = createBrowserRouter([
  {
    path: "/login",
    errorElement: <RouteErrorPage/>,
    element: <LazyBoundary><LoginPage/></LazyBoundary>
  },
  {
    path: "/",
    errorElement: <RouteErrorPage/>,
    element: <Navigate to="/login" replace/>
  },
  {
    path: "/dashboard",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><DashboardPage/></ProtectedRoute>
  },
  {
    path: "/influencers",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><InfluencersPage/></ProtectedRoute>
  },
  {
    path: "/influencers/blocked",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><BlockedInfluencersPage/></ProtectedRoute>
  },
  {
    path: "/influencers/rejected",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><RejectedInfluencersPage/></ProtectedRoute>
  },
  {
    path: "/influencers/:id",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><InfluencerDetailPage/></ProtectedRoute>
  },
  {
    path: "/influencers/:id/edit",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><InfluencerEditPage/></ProtectedRoute>
  },
  {
    path: "/influencer-contacts",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><InfluencerContactsPage/></ProtectedRoute>
  },
  {
    path: "/influencer-statistics",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><InfluencerStatisticsPage/></ProtectedRoute>
  },
  {
    path: "/events",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><EventsPage/></ProtectedRoute>
  },
  {
    path: "/events/create",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><CreateEventPage/></ProtectedRoute>
  },
  {
    path: "/events/:id",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><EventDetailPage/></ProtectedRoute>
  },
  {
    path: "/events/:id/edit",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><EditEventPage/></ProtectedRoute>
  },
  {
    path: "/events/:id/task-submissions",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><EventTaskSubmissionsPage/></ProtectedRoute>
  },
  {
    path: "/signup-requests",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><SignUpRequestsPage/></ProtectedRoute>
  },
  {
    path: "/signup-requests/organizations",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><OrganizationSignUpRequestsPage/></ProtectedRoute>
  },
  {
    path: "/event-requests",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><EventRequestsPage/></ProtectedRoute>
  },
  {
    path: "/categories",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><CategoriesPage/></ProtectedRoute>
  },
  {
    path: "/banners",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><BannersPage/></ProtectedRoute>
  },
  {
    path: "/academy",
    element: <ProtectedRoute><AcademyPage/></ProtectedRoute>
  },
  {
    path: "/academy/courses/:id",
    element: <ProtectedRoute><AcademyCourseDetailPage/></ProtectedRoute>
  },
  {
    path: "/countries",
    element: <ProtectedRoute><CountriesPage/></ProtectedRoute>
  },
  {
    path: "/signup-requests/:id",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><RequestDetailPage/></ProtectedRoute>
  },
  {
    path: "/signup-requests/:id/edit",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><RequestEditPage/></ProtectedRoute>
  },
  {
    path: "/signup-requests/influencer/:id",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><InfluencerRequestDetailPage/></ProtectedRoute>
  },
  {
    path: "/signup-requests/influencer/:id/edit",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><InfluencerRequestEditPage/></ProtectedRoute>
  },
  {
    path: "/signup-requests/organization/:id",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><OrganizationDetail/></ProtectedRoute>
  },
  {
    path: "/event-requests/:id",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><EventRequestDetailPage/></ProtectedRoute>
  },
  {
    path: "/event-requests/:id/edit",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><EventRequestEditPage/></ProtectedRoute>
  },
  {
    path: "/organizations",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><OrganizationsPage/></ProtectedRoute>
  },
  {
    path: "/organizations/:id",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><OrganizationDetail/></ProtectedRoute>
  },
  {
    path: "/organizations/:organizationId/branches/:branchId",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><BranchDetailPage/></ProtectedRoute>
  },
  {
    path: "/leads",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><LeadsPage/></ProtectedRoute>
  },
  {
    path: "/leads/:id",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><LeadDetail/></ProtectedRoute>
  },
  {
    path: "/settings",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><SettingsPage/></ProtectedRoute>
  },
  {
    path: "/admins",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><AdminManagementPage/></ProtectedRoute>
  },
  {
    path: "/signup-attempts",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><SignUpAttemptsPage/></ProtectedRoute>
  },
  {
    path: "/signup-attempts/journey",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><SignUpJourneyDetailPage/></ProtectedRoute>
  },
  {
    path: "/instagram/login",
    errorElement: <RouteErrorPage/>,
    element: <LazyBoundary><InstagramLoginPage/></LazyBoundary>
  },
  {
    path: "/instagram/callback",
    errorElement: <RouteErrorPage/>,
    element: <LazyBoundary><InstagramCallbackPage/></LazyBoundary>
  },
  {
    path: "/instagram/callback/welcome",
    errorElement: <RouteErrorPage/>,
    element: <LazyBoundary><InstagramCallbackWelcomePage/></LazyBoundary>
  },
  {
    path: "/instagram/insights",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><InstagramInsightsPage/></ProtectedRoute>
  },
  {
    path: "/finance/withdrawals",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><WithdrawalRequestsPage/></ProtectedRoute>
  },
  {
    path: "/finance/withdrawals/:id",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><WithdrawalDetailPage/></ProtectedRoute>
  },
  {
    path: "/finance/transactions",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><TransactionsPage/></ProtectedRoute>
  },
  {
    path: "/rewards",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><RewardsPage/></ProtectedRoute>
  },
  {
    path: "/withdrawals",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><WithdrawalRequestsV2Page/></ProtectedRoute>
  },
  {
    path: "/influencers/:id/wallet",
    errorElement: <RouteErrorPage/>,
    element: <ProtectedRoute><InfluencerWalletPage/></ProtectedRoute>
  },
  {
    path: "*",
    element: <NotFoundPage/>
  }
])

/**
 * QueryClient configured with proper defaults and error handling
 * Created outside component to prevent recreation on remounts
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      onError: (error) => {
        console.error('Mutation error:', error);
        toast({
          title: 'Error',
          description: toApiError(error).message,
          variant: 'destructive',
        });
      },
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider attribute="class" defaultTheme="light" enableSystem disableTransitionOnChange>
            <YMaps query={{ apikey: YANDEX_MAPS_API_KEY }}>
              <RouterProvider router={router}/>
              <Toaster />
            </YMaps>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
